import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { FiX } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import api from '../../../system/client/src/services/api';
import PageShell from '../../Components/PageShell';
import Modal from '../../Components/Modal';
import CommodityFilter from './Components/CommodityFilter';
import TradersTable from './Components/TradersTable';
import BatchProgressOverlay from '../../Components/BatchProgressOverlay';
import Hint from '../../Components/Hint';
import SizeFilter from './Components/SizeFilter';
import { matchesSize, offeredSizes } from './companySize';
import CustomsFilter from './Components/CustomsFilter';
import BusinessTypeFilter from './Components/BusinessTypeFilter';
import RegionFilter from './Components/RegionFilter';
import { matchesType, offeredTypes } from './businessType';
import { matchesTreatments, offeredTreatments, useCustomsEnabled } from './customsTreatments';
import useHints from '../../Components/useHints';
import { postcodeKind, validatePostcode, canCentreRadius, areaOf } from './postcodeInput';
import './TradersPage.css';

// The map pulls in MapLibre (~800kB); only load it for people who open it.
const TradersMap = lazy(() => import('./Components/TradersMap'));

const STORAGE_KEYS = {
    TRADERS_DATA: 'traders_search_results',
    FILTERS: 'traders_filters',
    LAST_SEARCH: 'traders_last_search',
    SORT_ORDER: 'traders_sort_order_v2',
    VIEW: 'traders_view', // 'list' | 'map' — whichever was used last opens next time
};

const RADIUS_OPTIONS = [5, 10, 15, 20, 25];
// Radius applied for you when you type a postcode and your plan has radius
// search (Pro and trial). You can pull it in, or switch it off.
const DEFAULT_RADIUS = 25;

const EMPTY_FILTERS = {
    companyName: '',
    postCode: '',
    // A town, council area, county or region picked from the postcode box's
    // suggestions: { key: 'district:E06000023', name, kindLabel, parent }.
    // The box then shows its name; typing again drops it.
    place: null,
    radiusMiles: '',
    // Regions from the Region picker, [{ key: 'region:E12000009', name }] —
    // the other way to say where. Choosing regions clears the postcode/place
    // and typing a postcode clears the regions.
    regions: [],
    minImports: 0,
    minExports: 0,
    minImportCustoms: 0,
    minExportCustoms: 0,
    hs2Codes: [],
    excludeHs2Codes: [],
    sizeBands: [], // company size from Companies House; empty = any
    businessTypes: [], // broad groups of Companies House SIC codes; empty = any
    customsTreatments: [], // border treatments their goods might need (HMRC tariff); empty = any
};

const loadStored = (key, fallback) => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return fallback;
        try {
            return JSON.parse(raw);
        } catch {
            return raw;
        }
    } catch {
        return fallback;
    }
};

const storeJson = (key, value) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        /* ignore */
    }
};

const TradersPage = () => {
    const customsOn = useCustomsEnabled(); // org setting — hides the border filter when off
    const [filters, setFilters] = useState(() => {
        const stored = { ...EMPTY_FILTERS, ...loadStored(STORAGE_KEYS.FILTERS, {}) };
        return { ...stored, sizeBands: offeredSizes(stored.sizeBands), businessTypes: offeredTypes(stored.businessTypes), customsTreatments: offeredTreatments(stored.customsTreatments) };
    });

    const [allTraders, setAllTraders] = useState(() =>
        loadStored(STORAGE_KEYS.TRADERS_DATA, []),
    );
    const [loading, setLoading] = useState(false);
    const [sortBy, setSortBy] = useState(() =>
        loadStored(STORAGE_KEYS.SORT_ORDER, 'import-customs-desc'),
    );
    const [lastSearch, setLastSearch] = useState(() =>
        loadStored(STORAGE_KEYS.LAST_SEARCH, null),
    );
    const [postcodeError, setPostcodeError] = useState(null);
    const [view, setView] = useState(() =>
        loadStored(STORAGE_KEYS.VIEW, 'list') === 'map' ? 'map' : 'list',
    );
    const hints = useHints();

    const [savedSearches, setSavedSearches] = useState([]);
    const [deletingSearch, setDeletingSearch] = useState(null); // the saved search awaiting "Delete?"
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [loadedFilterId, setLoadedFilterId] = useState(null);
    const [commodityLists, setCommodityLists] = useState([]);

    const [saveOpen, setSaveOpen] = useState(false);
    const [saveName, setSaveName] = useState('');
    const [saveDescription, setSaveDescription] = useState('');


    const [progressVisible, setProgressVisible] = useState(false);
    const [progressData, setProgressData] = useState({
        currentBatch: 0,
        totalBatches: 0,
        status: '',
        progress: 0,
        currentPatterns: [],
    });
    const eventSourceRef = useRef(null);
    const resetPaginationRef = useRef(null);

    // Can this org use radius search? Pro, trial, or an admin.
    const [canRadius, setCanRadius] = useState(false);
    // True while the radius is one we set by default; stops once the user
    // picks a radius (or Off) themselves.
    const radiusAutoRef = useRef(false);
    // "Did you mean" names for a name search that found nothing.
    const [suggestions, setSuggestions] = useState([]);
    // Places matching what is typed in the postcode box, and which is highlighted.
    const [placeOptions, setPlaceOptions] = useState([]);
    const [placeActive, setPlaceActive] = useState(-1);
    const placeFetchRef = useRef(0);
    // Set by the one-click fixes: search once the new filters are in state.
    const [searchQueued, setSearchQueued] = useState(false);

    // Can the map reload its area as it moves? (Pro/trial — each move is a search.)
    const [canMapArea, setCanMapArea] = useState(false);
    // How many months of trade history the plan shows (12 Pro/trial, 6 free):
    // the most "min import/export months" can usefully be.
    const [historyMonths, setHistoryMonths] = useState(12);

    useEffect(() => {
        api.get('subscription')
            .then((sub) => {
                setCanRadius(Boolean(sub?.adminBypass || sub?.limits?.radiusSearch));
                setCanMapArea(Boolean(sub?.adminBypass || sub?.limits?.mapAreaSearch));
                const months = sub?.adminBypass ? sub?.plans?.pro?.historyMonths : sub?.limits?.historyMonths;
                if (Number(months) > 0) setHistoryMonths(Number(months));
            })
            .catch(() => {});
    }, []);

    // Pull any stored or saved-search month threshold down to the plan's history.
    useEffect(() => {
        setFilters((f) => (f.minImports > historyMonths || f.minExports > historyMonths
            ? { ...f, minImports: Math.min(f.minImports || 0, historyMonths), minExports: Math.min(f.minExports || 0, historyMonths) }
            : f));
    }, [historyMonths, filters.minImports, filters.minExports]);

    const isRadiusSearch =
        Boolean(filters.postCode) && Number(filters.radiusMiles) > 0;

    /* With no company name, postcode or place, the business type, size and
     * commodity filters are what the search finds traders by (UK-wide, or in
     * the chosen regions) — so any one of them is enough to search, and
     * changing one needs a new search. Otherwise they only narrow the
     * results already loaded, as before. */
    const filtersOnly = !filters.companyName && !filters.postCode && !filters.place;
    const hasCriteria = Boolean(
        filters.regions?.length ||
            (filtersOnly && (filters.businessTypes?.length || filters.sizeBands?.length || filters.hs2Codes?.length)),
    );

    const hasPrimaryFilter =
        Boolean(filters.companyName) ||
        Boolean(filters.place) ||
        (Boolean(filters.postCode) && !postcodeError) ||
        hasCriteria;

    const hasRefineFilters = Boolean(
        filters.minImports > 0 ||
            filters.minExports > 0 ||
            filters.minImportCustoms > 0 ||
            filters.minExportCustoms > 0 ||
            (filters.hs2Codes && filters.hs2Codes.length > 0) ||
            (filters.excludeHs2Codes && filters.excludeHs2Codes.length > 0) ||
            (filters.sizeBands && filters.sizeBands.length > 0) ||
            (filters.businessTypes && filters.businessTypes.length > 0) ||
            (customsOn && filters.customsTreatments && filters.customsTreatments.length > 0),
    );

    const hasAnyFilter = Boolean(
        filters.companyName ||
            filters.postCode ||
            filters.regions?.length ||
            filters.radiusMiles ||
            hasRefineFilters,
    );

    const currentSearchSig = useMemo(
        () => ({
            companyName: filters.companyName || '',
            postCode: filters.postCode || '',
            place: filters.place?.key || null,
            radiusMiles: isRadiusSearch ? Number(filters.radiusMiles) : null,
            regions: (filters.regions || []).map((r) => r.key).sort().join(','),
            criteria: filtersOnly
                ? [filters.businessTypes, filters.sizeBands, filters.hs2Codes].map((l) => [...(l || [])].sort().join(',')).join('|')
                : '',
        }),
        [filters.companyName, filters.postCode, filters.place, filters.radiusMiles, isRadiusSearch,
            filters.regions, filtersOnly, filters.businessTypes, filters.sizeBands, filters.hs2Codes],
    );

    const hasResultsForCurrent =
        allTraders.length > 0 &&
        lastSearch &&
        lastSearch.companyName === currentSearchSig.companyName &&
        lastSearch.postCode === currentSearchSig.postCode &&
        (lastSearch.place || null) === currentSearchSig.place &&
        lastSearch.radiusMiles === currentSearchSig.radiusMiles &&
        (lastSearch.regions || '') === currentSearchSig.regions &&
        (lastSearch.criteria || '') === currentSearchSig.criteria;

    /* On free searches the server sends the first 50 companies in full and
     * the rest masked (figures, size and area — no name). Refine filters and
     * sorting work on every row; masked rows can't be opened. */
    const openTraders = useMemo(() => (allTraders || []).filter((t) => !t.locked), [allTraders]);
    const lockedTraders = useMemo(() => (allTraders || []).filter((t) => t.locked), [allTraders]);

    const filteredTraders = useMemo(() => {
        if (!allTraders || allTraders.length === 0) return [];
        return allTraders.filter((t) => {
            if (filters.minImports > 0 && (t.import_count || 0) < filters.minImports)
                return false;
            if (filters.minExports > 0 && (t.export_count || 0) < filters.minExports)
                return false;
            if (
                filters.minImportCustoms > 0 &&
                (t.import_peak_activity || 0) < filters.minImportCustoms
            )
                return false;
            if (
                filters.minExportCustoms > 0 &&
                (t.export_peak_activity || 0) < filters.minExportCustoms
            )
                return false;

            // selected codes are mixed-level: 2 chars = HS2 chapter, 4 = HS4 heading
            const tradeCodes = new Set(t.locked ? t.hs_codes || [] : []);
            (t.trades || []).forEach((tr) => {
                if (tr.hs2_code) tradeCodes.add(tr.hs2_code);
                if (tr.hs4_code) tradeCodes.add(tr.hs4_code);
            });

            if (
                filters.hs2Codes &&
                filters.hs2Codes.length > 0 &&
                !filters.hs2Codes.some((c) => tradeCodes.has(c))
            )
                return false;

            if (
                filters.excludeHs2Codes &&
                filters.excludeHs2Codes.length > 0 &&
                filters.excludeHs2Codes.some((c) => tradeCodes.has(c))
            )
                return false;

            // Company size — mirrored in system/server/lib/searchFilters.js.
            if (!matchesSize(t, filters.sizeBands)) return false;
            // Business type — mirrored in system/server/lib/searchFilters.js.
            if (!matchesType(t, filters.businessTypes)) return false;
            // Border treatments — mirrored in system/server/lib/searchFilters.js.
            if (customsOn && !matchesTreatments(t, filters.customsTreatments)) return false;

            return true;
        });
    }, [allTraders, filters, customsOn]);

    useEffect(() => storeJson(STORAGE_KEYS.FILTERS, filters), [filters]);
    useEffect(() => storeJson(STORAGE_KEYS.SORT_ORDER, sortBy), [sortBy]);
    useEffect(() => storeJson(STORAGE_KEYS.VIEW, view), [view]);
    useEffect(() => {
        if (allTraders.length > 0) storeJson(STORAGE_KEYS.TRADERS_DATA, allTraders);
    }, [allTraders]);
    useEffect(() => storeJson(STORAGE_KEYS.LAST_SEARCH, lastSearch), [lastSearch]);

    useEffect(() => {
        return () => {
            if (eventSourceRef.current) eventSourceRef.current.close();
        };
    }, []);

    const fetchSavedSearches = async () => {
        try {
            const data = await api.get('saved-searches');
            setSavedSearches(data || []);
        } catch (e) {
            console.error('Error fetching saved searches:', e);
        }
    };

    useEffect(() => {
        fetchSavedSearches();
    }, []);

    useEffect(() => {
        api.get('commodity-lists')
            .then((data) => setCommodityLists(data || []))
            .catch((e) => console.error('Error fetching commodity lists:', e));
    }, []);

    /* Onboarding tour fires this event when it wants to pre-fill the search
     * sidebar (Step 2). Keeping the page authoritative over its own state
     * means we don't have to expose setFilters externally. */
    useEffect(() => {
        const onFill = (e) => {
            const next = e.detail || {};
            setFilters((f) => ({
                ...f,
                ...(next.companyName != null
                    ? { companyName: next.companyName }
                    : {}),
                ...(next.postCode != null
                    ? { postCode: next.postCode, place: null }
                    : {}),
            }));
            if (next.postCode != null) setPostcodeError(validatePostcode(next.postCode));
        };
        window.addEventListener('onboarding:fill-search', onFill);
        return () =>
            window.removeEventListener('onboarding:fill-search', onFill);
    }, []);

    const setFilter = (field) => (value) => {
        if (field === 'postCode') setPostcodeError(validatePostcode(value));
        if (field === 'companyName' || field === 'postCode') {
            if (!value) {
                setFilters((f) => ({
                    ...f,
                    [field]: value,
                    radiusMiles: field === 'postCode' ? '' : f.radiusMiles,
                    place: field === 'postCode' ? null : f.place,
                }));
                if (field === 'postCode') {
                    setPostcodeError(null);
                    setPlaceOptions([]);
                    radiusAutoRef.current = false;
                }
                return;
            }
        }
        if (field === 'postCode') {
            /* Default the radius to 25 miles for plans that have it, as soon
             * as the postcode can be a centre point. A bare area (B, BS) can't,
             * so a default radius is taken off again while typing past it. */
            setFilters((f) => {
                let radiusMiles = f.radiusMiles;
                if (!canCentreRadius(value)) {
                    radiusMiles = '';
                    radiusAutoRef.current = false;
                } else if (canRadius && (!f.radiusMiles || radiusAutoRef.current)) {
                    radiusMiles = DEFAULT_RADIUS;
                    radiusAutoRef.current = true;
                }
                return { ...f, postCode: value, place: null, radiusMiles, regions: [] };
            });
            loadPlaceOptions(value);
            return;
        }
        if (field === 'radiusMiles') {
            radiusAutoRef.current = false;
        }
        if (field === 'hs2Codes') {
            setFilters((f) => ({
                ...f,
                hs2Codes: value,
                excludeHs2Codes: (f.excludeHs2Codes || []).filter(
                    (c) => !value.includes(c),
                ),
            }));
            setLoadedFilterId(null);
            return;
        }
        if (field === 'excludeHs2Codes') {
            setFilters((f) => ({
                ...f,
                excludeHs2Codes: value,
                hs2Codes: (f.hs2Codes || []).filter((c) => !value.includes(c)),
            }));
            setLoadedFilterId(null);
            return;
        }
        if (
            ['minImports', 'minExports', 'minImportCustoms', 'minExportCustoms'].includes(
                field,
            )
        ) {
            const n = Number(value);
            let safe = Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
            // Months can't exceed the history the plan shows.
            if (field === 'minImports' || field === 'minExports') safe = Math.min(safe, historyMonths);
            setFilters((f) => ({ ...f, [field]: safe }));
            setLoadedFilterId(null);
            return;
        }
        setFilters((f) => ({ ...f, [field]: value }));
    };

    /* Town / council area / county / region suggestions for the postcode
     * box, once what's typed looks like a name. Latest keystroke wins. */
    const loadPlaceOptions = (value) => {
        const ticket = ++placeFetchRef.current;
        setPlaceActive(-1);
        if (postcodeKind(value) !== 'place') {
            setPlaceOptions([]);
            return;
        }
        api.get('traders/places', { params: { q: value.trim() } })
            .then((places) => {
                if (ticket === placeFetchRef.current) setPlaceOptions(places || []);
            })
            .catch(() => {});
    };

    const pickPlace = (p) => {
        placeFetchRef.current++;
        setPlaceOptions([]);
        setPlaceActive(-1);
        setPostcodeError(null);
        radiusAutoRef.current = false;
        setFilters((f) => ({
            ...f,
            postCode: p.name,
            place: { key: p.key, name: p.name, kindLabel: p.kindLabel, parent: p.parent },
            radiusMiles: '', // the place is the area — no radius round it
            regions: [],
        }));
    };

    const onPostcodeKeyDown = (e) => {
        if (placeOptions.length) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setPlaceActive((i) => (e.key === 'ArrowDown'
                    ? Math.min(i + 1, placeOptions.length - 1)
                    : Math.max(i - 1, -1)));
                return;
            }
            if (e.key === 'Escape') {
                setPlaceOptions([]);
                return;
            }
            if (e.key === 'Enter' && placeActive >= 0 && placeActive < placeOptions.length) {
                e.preventDefault();
                pickPlace(placeOptions[placeActive]);
                return;
            }
        }
        if (e.key === 'Enter') {
            setPlaceOptions([]);
            handleSearch();
        }
    };

    const buildSearchParams = () => {
        const params = {
            companyName: filters.companyName,
            postCode: filters.postCode,
            // On free searches, also send the companies past the result limit,
            // masked (older mobile builds don't ask, so don't get them).
            locked: 1,
        };
        if (filters.place) params.place = filters.place.key;
        if (filters.regions?.length) params.regions = filters.regions.map((r) => r.key);
        if (filtersOnly) {
            if (filters.businessTypes?.length) params.businessTypes = filters.businessTypes;
            if (filters.sizeBands?.length) params.sizeBands = filters.sizeBands;
            if (filters.hs2Codes?.length) params.hs2Codes = filters.hs2Codes;
        }
        if (isRadiusSearch) {
            params.originPostCode = filters.postCode;
            params.radiusMiles = Number(filters.radiusMiles);
        }
        return params;
    };

    /* The map reloads its visible area as it moves (Pro/trial only — the
     * map doesn't call this otherwise). Latest move wins: an answer for an
     * area the user has already left is dropped. */
    const [mapLoading, setMapLoading] = useState(false);
    const [mapError, setMapError] = useState(null);
    const mapRequestRef = useRef(0);
    const runMapSearch = async ({ south, west, north, east }) => {
        const requestId = ++mapRequestRef.current;
        setMapLoading(true);
        setMapError(null);
        try {
            const params = { bounds: [south, west, north, east].map((v) => v.toFixed(5)).join(',') };
            if (filters.companyName) params.companyName = filters.companyName;
            const data = await api.get('traders', { params });
            if (requestId !== mapRequestRef.current) return;
            setAllTraders(data || []);
            setSuggestions([]);
            setLastSearch({
                companyName: filters.companyName || '',
                postCode: '',
                radiusMiles: null,
                fromMap: true,
            });
        } catch (e) {
            if (requestId !== mapRequestRef.current) return;
            setMapError(e?.response?.data?.error || 'Could not load this area');
        } finally {
            if (requestId === mapRequestRef.current) setMapLoading(false);
        }
    };

    // Only when a company-name search found nothing at all.
    const loadSuggestions = (results) => {
        setSuggestions([]);
        if ((results || []).length || !filters.companyName) return;
        api.get('traders/suggest', { params: { companyName: filters.companyName } })
            .then((names) => setSuggestions(names || []))
            .catch(() => {});
    };

    const runRegularSearch = async () => {
        try {
            setLoading(true);
            const params = buildSearchParams();
            const data = await api.get('traders', {
                params,
                paramsSerializer: {
                    serialize: (p) => {
                        const sp = new URLSearchParams();
                        Object.entries(p).forEach(([k, v]) => {
                            if (Array.isArray(v)) v.forEach((it) => sp.append(k, it));
                            else if (v != null) sp.append(k, v);
                        });
                        return sp.toString();
                    },
                },
            });
            setAllTraders(data || []);
            setLastSearch(currentSearchSig);
            loadSuggestions(data);
        } catch (e) {
            console.error('Error fetching traders:', e);
            message.error('Search failed');
        } finally {
            setLoading(false);
        }
    };

    const runBatchedSearch = () => {
        if (eventSourceRef.current) eventSourceRef.current.close();
        const params = buildSearchParams();
        setProgressVisible(true);
        setProgressData({
            currentBatch: 0,
            totalBatches: 1,
            status: 'Starting search…',
            progress: 0,
            currentPatterns: [],
        });
        eventSourceRef.current = api.getBatchedTraders(
            params,
            (pd) => {
                setProgressData({
                    ...pd,
                    currentBatch: Number(pd.currentBatch) || 0,
                    totalBatches: Number(pd.totalBatches) || 0,
                    progress: Number(pd.progress) || 0,
                });
            },
            (traders) => {
                setAllTraders(traders || []);
                setLastSearch(currentSearchSig);
                loadSuggestions(traders);
                setProgressVisible(false);
                setLoading(false);
            },
            (err) => {
                setProgressVisible(false);
                setLoading(false);
                // Too many traders to list: say so where the results go.
                if (err.code === 'place_too_wide') {
                    setAllTraders([]);
                    setLastSearch({ ...currentSearchSig, tooWide: err.message });
                    return;
                }
                console.error('Batched search error:', err);
                message.error(`Search failed: ${err.message || 'unknown error'}`);
                setProgressVisible(false);
                setLoading(false);
            },
        );
    };

    const handleSearch = () => {
        if (postcodeError && !filters.place) return;
        if (!hasPrimaryFilter) return;
        if (resetPaginationRef.current) resetPaginationRef.current.current?.();

        const kind = postcodeKind(filters.postCode);
        const broad = isRadiusSearch || Boolean(filters.place) || kind === 'area' || kind === 'place' || hasCriteria;

        if (broad) runBatchedSearch();
        else runRegularSearch();
    };

    useEffect(() => {
        if (!searchQueued) return;
        setSearchQueued(false);
        handleSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQueued]);

    // One-click fix: change the filters, then search again.
    const retryWith = (changes) => {
        radiusAutoRef.current = false;
        setFilters((f) => ({ ...f, ...('postCode' in changes ? { place: null } : {}), ...changes }));
        if ('postCode' in changes) setPostcodeError(validatePostcode(changes.postCode));
        setSearchQueued(true);
    };

    const handleClear = () => {
        setAllTraders([]);
        setLastSearch(null);
        setFilters(EMPTY_FILTERS);
        setPostcodeError(null);
        setLoadedFilterId(null);
        setSuggestions([]);
        setPlaceOptions([]);
        radiusAutoRef.current = false;
        storeJson(STORAGE_KEYS.TRADERS_DATA, []);
        message.success('Cleared');
    };

    const handleLoadSavedSearch = (search) => {
        const saved = search.search_filters || {};
        setFilters((f) => ({
            ...f,
            companyName: 'companyName' in saved ? saved.companyName || '' : f.companyName,
            postCode: 'postCode' in saved ? saved.postCode || '' : f.postCode,
            place: 'postCode' in saved ? saved.place || null : f.place,
            regions: 'postCode' in saved ? saved.regions || [] : f.regions,
            radiusMiles: 'radiusMiles' in saved ? saved.radiusMiles || '' : f.radiusMiles,
            minImports: saved.minImports || 0,
            minExports: saved.minExports || 0,
            minImportCustoms: saved.minImportCustoms || 0,
            minExportCustoms: saved.minExportCustoms || 0,
            hs2Codes: saved.hs2Codes || [],
            excludeHs2Codes: saved.excludeHs2Codes || [],
            sizeBands: offeredSizes(saved.sizeBands),
            businessTypes: offeredTypes(saved.businessTypes),
            customsTreatments: offeredTreatments(saved.customsTreatments),
        }));
        setPostcodeError(saved.place ? null : validatePostcode(saved.postCode || ''));
        setLoadedFilterId(search.id);
        message.success(`Loaded "${search.name}"`);
    };

    const handleDeleteSaved = async () => {
        const s = deletingSearch;
        if (!s) return;
        setDeleteBusy(true);
        try {
            await api.delete(`saved-searches/${s.id}`);
            setSavedSearches((prev) => prev.filter((x) => x.id !== s.id));
            if (loadedFilterId === s.id) setLoadedFilterId(null);
            setDeletingSearch(null);
            message.success(`Deleted "${s.name}"`);
        } catch {
            message.error('Could not delete that saved search');
        } finally {
            setDeleteBusy(false);
        }
    };

    const handleSaveCurrent = async () => {
        if (!saveName.trim()) {
            message.error('Please name the filter');
            return;
        }
        try {
            await api.post('saved-searches', {
                name: saveName.trim(),
                description: saveDescription.trim(),
                filters: {
                    companyName: filters.companyName || '',
                    postCode: filters.postCode || '',
                    place: filters.place || null,
                    radiusMiles: filters.radiusMiles || '',
                    minImports: filters.minImports,
                    minExports: filters.minExports,
                    minImportCustoms: filters.minImportCustoms,
                    minExportCustoms: filters.minExportCustoms,
                    hs2Codes: filters.hs2Codes,
                    excludeHs2Codes: filters.excludeHs2Codes,
                    sizeBands: filters.sizeBands || [],
                    businessTypes: filters.businessTypes || [],
                    regions: filters.regions || [],
                    customsTreatments: filters.customsTreatments || [],
                },
            });
            message.success('Filter saved');
            setSaveOpen(false);
            setSaveName('');
            setSaveDescription('');
            fetchSavedSearches();
        } catch (e) {
            console.error('Error saving filter:', e);
            message.error('Failed to save filter');
        }
    };

    const sidebar = (
        <>
          <div className="dash-side-scroll">
            <div className="group">
                <span className="group-label">Saved searches</span>
                {savedSearches.length === 0 ? (
                    <span className="saved-search-empty">No saved filters</span>
                ) : (
                    savedSearches.map((s) => (
                        <div key={s.id} className="saved-search-row">
                            <button
                                type="button"
                                className={`saved-search ${loadedFilterId === s.id ? 'on' : ''}`}
                                onClick={() => handleLoadSavedSearch(s)}
                            >
                                <span>
                                    {s.name}
                                    {s.description ? (
                                        <span className="desc">{s.description}</span>
                                    ) : null}
                                </span>
                            </button>
                            <button
                                type="button"
                                className="saved-search-delete"
                                aria-label={`Delete saved search ${s.name}`}
                                title="Delete"
                                onClick={() => setDeletingSearch(s)}
                            >
                                <FiX />
                            </button>
                        </div>
                    ))
                )}
            </div>

            <div className="group">
                <span className="group-label">Search</span>
                <div className="filter-group">
                    <div className="form-field">
                        <label htmlFor="f-company">Company name</label>
                        <input
                            id="f-company"
                            type="text"
                            placeholder="e.g. Acme Ltd"
                            value={filters.companyName}
                            onChange={(e) => setFilter('companyName')(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        />
                    </div>
                    <div className="form-field place-field" data-onboard="search-postcode">
                        <label htmlFor="f-postcode">Postcode or place</label>
                        <input
                            id="f-postcode"
                            type="text"
                            placeholder="BS1, BS1 1AA, a town, city or county"
                            autoComplete="off"
                            role="combobox"
                            aria-expanded={placeOptions.length > 0}
                            aria-controls="f-postcode-places"
                            value={filters.postCode}
                            onChange={(e) => setFilter('postCode')(e.target.value)}
                            onKeyDown={onPostcodeKeyDown}
                            onBlur={() => setTimeout(() => setPlaceOptions([]), 150)}
                        />
                        {placeOptions.length > 0 && (
                            <ul id="f-postcode-places" className="place-suggest" role="listbox">
                                {placeOptions.map((p, i) => (
                                    <li
                                        key={p.key}
                                        role="option"
                                        aria-selected={i === placeActive}
                                        className={i === placeActive ? 'on' : ''}
                                        onMouseDown={(e) => { e.preventDefault(); pickPlace(p); }}
                                    >
                                        <span className="place-name">{p.name}</span>
                                        <span className="place-kind">
                                            {p.kindLabel}
                                            {p.parent && !p.name.includes('(') ? ` · ${p.parent}` : ''}
                                        </span>
                                        <span className="place-count">{p.traders.toLocaleString()}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {filters.place ? (
                            <span className="place-chosen">
                                Every trader in this {filters.place.kindLabel}
                            </span>
                        ) : postcodeError && (
                            <span className="form-error">{postcodeError}</span>
                        )}
                    </div>
                    <div className="form-field">
                        <label htmlFor="f-radius">Radius</label>
                        <select
                            id="f-radius"
                            value={filters.radiusMiles || ''}
                            onChange={(e) =>
                                setFilter('radiusMiles')(
                                    e.target.value ? Number(e.target.value) : '',
                                )
                            }
                            disabled={Boolean(filters.place) || !canCentreRadius(filters.postCode)}
                        >
                            <option value="">Off</option>
                            {RADIUS_OPTIONS.map((m) => (
                                <option key={m} value={m}>
                                    {m} miles
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="search-or">or</div>
                    <div className="form-field">
                        <label>Regions</label>
                        <RegionFilter
                            value={filters.regions || []}
                            onChange={(v) => {
                                if (v.length) {
                                    setPostcodeError(null);
                                    setPlaceOptions([]);
                                    radiusAutoRef.current = false;
                                }
                                setFilters((f) => ({
                                    ...f,
                                    regions: v,
                                    ...(v.length ? { postCode: '', place: null, radiusMiles: '' } : {}),
                                }));
                                setLoadedFilterId(null);
                            }}
                        />
                    </div>
                    <div className="form-field">
                        <label>Company size</label>
                        <SizeFilter
                            value={filters.sizeBands || []}
                            onChange={(v) => setFilters((f) => ({ ...f, sizeBands: v }))}
                        />
                    </div>
                    <div className="form-field">
                        <label title="From the company's Companies House activity codes">Business type</label>
                        <BusinessTypeFilter
                            value={filters.businessTypes || []}
                            onChange={(v) => setFilters((f) => ({ ...f, businessTypes: v }))}
                        />
                    </div>
                    {customsOn && (
                        <div className="form-field">
                            <label title="Traders whose goods might need any of these at the border, from the HMRC tariff">
                                Might need at the border
                            </label>
                            <CustomsFilter
                                value={filters.customsTreatments || []}
                                onChange={(v) => setFilters((f) => ({ ...f, customsTreatments: v }))}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="group">
                <span className="group-label">Refine results</span>
                <div className="filter-group">
                    <div className="num-row">
                        <label htmlFor="f-mi">Min import months <span className="num-of">of {historyMonths}</span></label>
                        <input
                            id="f-mi"
                            type="number"
                            min="0"
                            max={historyMonths}
                            title={`Up to ${historyMonths} — your plan shows ${historyMonths} months of trade history`}
                            value={filters.minImports || 0}
                            onChange={(e) => setFilter('minImports')(e.target.value)}
                        />
                    </div>
                    <div className="num-row">
                        <label htmlFor="f-mic">Min import entries</label>
                        <input
                            id="f-mic"
                            type="number"
                            min="0"
                            value={filters.minImportCustoms || 0}
                            onChange={(e) =>
                                setFilter('minImportCustoms')(e.target.value)
                            }
                        />
                    </div>
                    <div className="num-row">
                        <label htmlFor="f-me">Min export months <span className="num-of">of {historyMonths}</span></label>
                        <input
                            id="f-me"
                            type="number"
                            min="0"
                            max={historyMonths}
                            title={`Up to ${historyMonths} — your plan shows ${historyMonths} months of trade history`}
                            value={filters.minExports || 0}
                            onChange={(e) => setFilter('minExports')(e.target.value)}
                        />
                    </div>
                    <div className="num-row">
                        <label htmlFor="f-mec">Min export entries</label>
                        <input
                            id="f-mec"
                            type="number"
                            min="0"
                            value={filters.minExportCustoms || 0}
                            onChange={(e) =>
                                setFilter('minExportCustoms')(e.target.value)
                            }
                        />
                    </div>
                    <div className="form-field">
                        <label>Commodity</label>
                        <CommodityFilter
                            value={filters.hs2Codes}
                            onChange={(v) => setFilter('hs2Codes')(v)}
                            placeholder="Include codes…"
                            variant="accent"
                            disabledValues={filters.excludeHs2Codes}
                            quickLists={commodityLists}
                        />
                    </div>
                    <div className="form-field">
                        <label>Exclude commodity</label>
                        <CommodityFilter
                            value={filters.excludeHs2Codes}
                            onChange={(v) => setFilter('excludeHs2Codes')(v)}
                            placeholder="Exclude codes…"
                            variant="default"
                            disabledValues={filters.hs2Codes}
                            quickLists={commodityLists}
                        />
                    </div>
                </div>
            </div>

          </div>

            <div className="dash-side-actions">
                <button
                    type="button"
                    data-onboard="search-button"
                    className="btn btn-primary"
                    onClick={handleSearch}
                    disabled={
                        !hasPrimaryFilter ||
                        Boolean(postcodeError) ||
                        loading ||
                        hasResultsForCurrent
                    }
                >
                    {loading
                        ? 'Searching…'
                        : hasResultsForCurrent
                          ? 'Results loaded'
                          : 'Search'}
                </button>
                <div className="row">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setSaveOpen(true)}
                        disabled={!hasAnyFilter}
                    >
                        Save filter
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleClear}
                    >
                        Clear
                    </button>
                </div>
            </div>
        </>
    );

    const headingBits = [];
    if (filters.companyName) headingBits.push(`"${filters.companyName}"`);
    if (lastSearch?.fromMap) {
        headingBits.push('Map area');
    } else if (filters.place) {
        headingBits.push(`${filters.place.name} (${filters.place.kindLabel})`);
    } else if (filters.regions?.length) {
        headingBits.push(filters.regions.map((r) => r.name).join(', '));
    } else if (filters.postCode) {
        headingBits.push(
            isRadiusSearch
                ? `${filters.postCode} +${filters.radiusMiles}mi`
                : filters.postCode,
        );
    }
    if (filters.hs2Codes?.length)
        headingBits.push(`HS ${filters.hs2Codes.join(', ')}`);
    const headingText =
        headingBits.length > 0 ? headingBits.join(' · ') : 'All traders.';

    /* Why the last search showed nothing, and one-click ways out. Only
     * built for a search that actually ran and came back empty. */
    const fixes = [];
    if (lastSearch && filteredTraders.length === 0) {
        if (allTraders.length > 0) {
            fixes.push({
                label: `Clear the refine filters (${allTraders.length} found before refining)`,
                run: () => setFilters((f) => ({
                    ...f,
                    minImports: 0, minExports: 0, minImportCustoms: 0, minExportCustoms: 0,
                    hs2Codes: [], excludeHs2Codes: [], sizeBands: [], businessTypes: [], customsTreatments: [],
                })),
            });
        } else {
            const radius = Number(lastSearch.radiusMiles) || 0;
            if (canRadius && !lastSearch.place && canCentreRadius(lastSearch.postCode) && radius < DEFAULT_RADIUS) {
                fixes.push({
                    label: `Widen to ${DEFAULT_RADIUS} miles around ${lastSearch.postCode.toUpperCase()}`,
                    run: () => retryWith({ radiusMiles: DEFAULT_RADIUS }),
                });
            }
            const area = areaOf(lastSearch.postCode);
            if (area && !radius) {
                fixes.push({
                    label: `Search the whole ${area} area`,
                    run: () => retryWith({ postCode: area, radiusMiles: '' }),
                });
            }
            // No "search anywhere" fix: the server already falls back to the
            // name alone when name + postcode finds nothing.
        }
    }

    const emptyContent = lastSearch && filteredTraders.length === 0 ? (
        <div className="search-help">
            {lastSearch.tooWide ? (
                <>
                    <span className="kicker">Too many to list</span>
                    <p>{lastSearch.tooWide}</p>
                </>
            ) : suggestions.length === 0 && (
                <>
                    <span className="kicker">No matches</span>
                    <p>
                        {allTraders.length > 0
                            ? 'Your refine filters hide every result.'
                            : 'Nothing matched that search.'}
                    </p>
                </>
            )}
            {suggestions.length > 0 && (
                /* The best guess is the headline, as a big button — a small
                 * list under "No matches" was easy to miss. */
                <div className="dym">
                    <span className="dym-label">Did you mean</span>
                    <Link to={`/traders/${suggestions[0].trader_id}`} className="dym-top">
                        <span className="dym-name">{suggestions[0].company_name}</span>
                        {suggestions[0].post_code ? (
                            <span className="dym-meta">{suggestions[0].post_code}</span>
                        ) : null}
                        <span className="dym-go">View trader →</span>
                    </Link>
                    {suggestions.length > 1 && (
                        <div className="dym-others">
                            <span className="muted">or</span>
                            {suggestions.slice(1).map((sg) => (
                                <Link key={sg.trader_id} to={`/traders/${sg.trader_id}`}>
                                    {sg.company_name}
                                    {sg.post_code ? <span className="muted"> · {sg.post_code}</span> : null}
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {fixes.length > 0 && (
                <div className="search-help-block">
                    <span className="search-help-label">Try</span>
                    <div className="search-help-fixes">
                        {fixes.map((fx) => (
                            <button key={fx.label} type="button" className="btn btn-secondary" onClick={fx.run}>
                                {fx.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {lastSearch.companyName && allTraders.length === 0 && (
                <p className="muted">
                    Still missing? Some companies import under a parent company&apos;s
                    name or through an agent, and HMRC doesn&apos;t publish every trader.
                </p>
            )}
        </div>
    ) : null;

    // Results that are not a straight match get a line saying so.
    const matchKind = allTraders.find((t) => t.match)?.match;
    const searchedPlace = lastSearch?.place
        ? `in ${lastSearch.postCode}`
        : lastSearch?.radiusMiles
          ? `within ${lastSearch.radiusMiles} miles of ${lastSearch.postCode?.toUpperCase()}`
          : `at ${lastSearch?.postCode?.toUpperCase()}`;
    const matchNote =
        matchKind === 'elsewhere'
            ? `No "${lastSearch?.companyName}" ${searchedPlace} — showing where they trade instead.`
            : matchKind === 'close'
              ? `No exact match for "${lastSearch?.companyName}" — showing close matches.`
              : null;

    const viewTabs = (
        <Hint
            id="results-map"
            title="See these on a map"
            text="Switch to Map to see every company as a pin. Click one for a quick look, and zoom in to load more. We'll remember which view you like."
            placement="bottom-end"
            show={view === 'list' && filteredTraders.length > 0}
        >
            <nav className="tab-bar results-view-tabs" aria-label="Results view">
                {[
                    { key: 'list', label: 'List' },
                    { key: 'map', label: 'Map' },
                ].map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        className={`tab ${view === t.key ? 'on' : ''}`}
                        onClick={() => {
                            setView(t.key);
                            // Found the map by themselves: the hint has done its job.
                            if (t.key === 'map') hints.dismiss('results-map');
                        }}
                    >
                        {t.label}
                    </button>
                ))}
            </nav>
        </Hint>
    );

    return (
        <PageShell sidebar={sidebar}>
            {/* One compact row in both views — heading, count and the
                List|Map switch — so switching views doesn't move things
                around and the map gets the rest of the screen. */}
            <div className="results-bar">
                <div className="results-bar-text">
                    <h1>{headingText}</h1>
                    <span className="results-bar-meta">
                        {lastSearch
                            ? `${filteredTraders.length.toLocaleString()} of ${allTraders.length.toLocaleString()} traders`
                            : 'Search by company, place or region — or just a business type, size or commodity'}
                    </span>
                    {matchNote && <span className="results-bar-note">{matchNote}</span>}
                </div>
                {viewTabs}
            </div>

            {view === 'map' && (filteredTraders.length > 0 || lastSearch?.fromMap) ? (
                <Suspense fallback={<div className="fs-map-loading">Loading map…</div>}>
                    <TradersMap
                        traders={filteredTraders}
                        resultsKey={lastSearch}
                        searching={mapLoading}
                        canLoadArea={canMapArea}
                        onAreaChange={runMapSearch}
                        areaError={mapError}
                    />
                </Suspense>
            ) : (
                <TradersTable
                    traders={filteredTraders}
                    allCount={allTraders.length}
                    lockedCount={lockedTraders.length}
                    openCount={openTraders.length}
                    loading={loading}
                    sortBy={sortBy}
                    onSortChange={setSortBy}
                    hasSearched={lastSearch !== null}
                    emptyContent={emptyContent}
                    onResetPaginationRef={(ref) => {
                        resetPaginationRef.current = ref;
                    }}
                />
            )}

            {/* Open Government Licence requires attribution wherever HMRC-derived data is shown. */}
            <p className="data-attribution">
                Contains HMRC data &copy; Crown copyright
                {view === 'map' && ' · Contains OS data © Crown copyright and database right'}
            </p>

            <Modal
                open={Boolean(deletingSearch)}
                onClose={() => !deleteBusy && setDeletingSearch(null)}
                kicker="Saved searches"
                title="Delete this saved search?"
                width={420}
                footer={
                    <>
                        <button type="button" className="btn btn-secondary" onClick={() => setDeletingSearch(null)} disabled={deleteBusy}>
                            Cancel
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleDeleteSaved} disabled={deleteBusy}>
                            {deleteBusy ? 'Deleting…' : 'Delete'}
                        </button>
                    </>
                }
            >
                <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-2)' }}>
                    &ldquo;{deletingSearch?.name}&rdquo; will be removed for everyone in your team. This can't be undone.
                </p>
            </Modal>

            <Modal
                open={saveOpen}
                onClose={() => setSaveOpen(false)}
                kicker="Saved searches"
                title="Save this search"
                footer={
                    <>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setSaveOpen(false)}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSaveCurrent}
                        >
                            Save
                        </button>
                    </>
                }
            >
                <div className="form-field">
                    <label htmlFor="save-name">Name</label>
                    <input
                        id="save-name"
                        type="text"
                        placeholder="e.g. High-volume importers"
                        value={saveName}
                        onChange={(e) => setSaveName(e.target.value)}
                        autoFocus
                    />
                </div>
                <div className="form-field" style={{ marginBottom: 0 }}>
                    <label htmlFor="save-description">Description (optional)</label>
                    <input
                        id="save-description"
                        type="text"
                        placeholder="A short note for later"
                        value={saveDescription}
                        onChange={(e) => setSaveDescription(e.target.value)}
                    />
                </div>
            </Modal>

            <BatchProgressOverlay
                visible={progressVisible}
                progress={progressData.progress}
                status={progressData.status}
                currentBatch={progressData.currentBatch}
                totalBatches={progressData.totalBatches}
                currentPatterns={progressData.currentPatterns}
            />
        </PageShell>
    );
};

export default TradersPage;
